<script setup>
// Platform-admin shell. Consumes saas-core's `vue/layouts/AdminLayout.vue`
// directly (since v0.5.0-alpha.5 the layout primitives — BaseShell,
// AdminLayout, ConsumerLayout, PageTabs — ship in saas-core after being
// lifted from i-Recall's PoC; local copies were deleted as part of the
// alpha.8 adoption pass per the Charter's loan-deletion discipline).
//
// Profile is intentionally NOT in the menu here. It moved to the consumer
// shell (ConsumerShell.vue) — profile is user-owned, not platform-admin.
// The link at the very bottom of the sidebar gives admins a route across to
// the consumer surface for their own settings.
import { useRouter } from "vue-router";
import AdminLayout from "@digitalstreams/saas-core/vue/layouts/AdminLayout.vue";
import { useAuthStore } from "@digitalstreams/saas-core/vue/stores/authStore";
import { adminMenu } from "@digitalstreams/saas-core/vue/admin/adminMenu";

const auth = useAuthStore();
const router = useRouter();

// Since alpha.5, adminMenu paths are root-relative without the legacy
// `/admin/` prefix (e.g. `to: '/tenants'` not `to: '/admin/tenants'`).
// i-Recall mounts its admin shell at `/ds-ops/*`, so we prepend that
// segment to compose the right vue-router targets.
//
// Hidden items:
//  - `/plans`, `/stripe-config`, `/email-config`, `/signup-config` are
//    bundled into the alpha.6 AdminSettings tabbed shell — surfaced via
//    the "Settings" sidebar entry rather than as individual menu items.
//  - `/subscriptions` and `/admins` aren't ready for this install yet
//    (Phase 2+ surfaces and the not-yet-mounted platform-admins router).
const HIDDEN = new Set([
  "/plans",
  "/stripe-config",
  "/email-config",
  "/signup-config",
  "/subscriptions",
  "/admins"
]);

function mapToSystemAdmin(path) {
  if (typeof path !== "string") return path;
  if (path === "/") return "/ds-ops";
  return "/ds-ops" + path;
}

const menu = adminMenu
  .map((section) => ({
    ...section,
    items: section.items
      .filter((it) => !HIDDEN.has(it.to))
      .map((it) => ({ ...it, to: mapToSystemAdmin(it.to) }))
  }))
  .filter((section) => section.items.length > 0);

// Platform-wide account lifecycle (alpha.13) isn't in saas-core's adminMenu
// manifest, so inject the nav entry explicitly. Distinct from the tenant
// "Users" item above (which manages members within a tenant).
menu.push({
  label: "Accounts",
  items: [
    { label: "Platform Users", to: "/ds-ops/platform-users", icon: "bi bi-person-gear" }
  ]
});

// Email Templates manager (alpha.15) — also not in saas-core's adminMenu
// manifest, so inject it. Flat editor for every system email's subject/body.
menu.push({
  label: "Email",
  items: [
    { label: "Email Templates", to: "/ds-ops/email-templates", icon: "bi bi-envelope-paper" }
  ]
});

// AI Configuration — i-Recall's role→service mapping + provider connection
// checks. Not part of saas-core's adminMenu manifest, so inject it.
menu.push({
  label: "AI",
  items: [
    { label: "AI Configuration", to: "/ds-ops/ai-config", icon: "bi bi-robot" }
  ]
});

async function onLogout() {
  await auth.logout();
  router.push("/topics"); // land on the public demo, not a login wall
}
</script>

<template>
  <AdminLayout
    app-name="i-Recall"
    home-to="/ds-ops"
    :user="auth.user"
    :menu="menu"
    @logout="onLogout"
  >
    <router-view />
  </AdminLayout>
</template>
